import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAccount } from '../../contexts/Account';
import ACSnackbar from '../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import {
  aobfStep3Mobile,
} from '../../services/company_services';

const styles = () => ({
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

const skillOptions = [
  { label: '📣 Marketing', value: 'sk_1021' },
  { label: '🤝 Sales', value: 'sk_1083' },
  { label: '⚙️ Operations', value: 'sk_1672' },
  // { label: '🧑‍🤝‍🧑 HR', value: 'sk_1322' },
  { label: '📦 Product', value: 'sk_1343' },
  { label: '💎 Finance', value: 'sk_1670' },
  // { label: '🧭 General Leadership', value: 'sk_1674' },
  // { label: '🚀 Entrepreneurship', value: 'sk_921' },
  // { label: '🎯 Career', value: 'sk_1553' },
  { label: '🧩 Other', value: 'none' },
];

function CompanyOnboardingStep3() {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = styles();
  const history = useHistory();

  const [skill, setSkill] = useState(
    accountDetails.desiredSkillsTagIds?.[0] || ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const disableButton = () => isLoading || !skill;

  async function sendFormData() {
    try {
      await aobfStep3Mobile({
        CONTACT_ID: asLoggedUserDetails.userId,
        SKILL_TAGS_IDS: skill === 'none' ? [] : [skill],
      });

      return {
        desiredSkillsTagIds: skill === 'none' ? [] : [skill],
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
      return {};
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const [payload] = await Promise.all([
        sendFormData(),
      ]);

      editAccountInformation({ ...payload });

      history.push('/company/onboarding/step4');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 3/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(3 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#000000',
          mb: 1,
        }}
      >
        How can advisors help?
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.43px',
          color: '#646D7A',
          mb: 3,
        }}
      >
        Select the primary type of experience that would be most helpful to have
        on your advisory board.
      </Typography>

      <Grid>
        <Grid {...classes.gridItemProps} mb="16px">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {skillOptions.map((option) => {
              const isSelected = skill === option.value;
              return (
                <ButtonBase
                  key={option.value}
                  onClick={() => setSkill(option.value)}
                  disableRipple
                  aria-pressed={isSelected}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    borderRadius: '12px',
                    px: 2,
                    py: 1.5,
                    border: '1px solid #E0E7F1',
                    backgroundColor: isSelected ? '#E4E9F1' : '#F7F9FC',
                    transition: 'background-color 0.2s ease',
                    textAlign: 'left',
                    '&:hover': {
                      backgroundColor: isSelected ? '#DCE3EE' : '#E6EBF3',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#111827',
                      letterSpacing: '-0.3px',
                    }}
                  >
                    {option.label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Grid>
        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={disableButton()}
          sx={{
            borderRadius: '15px',
            backgroundColor: '#3171F6',
            '&:hover': {
              backgroundColor: '#3171F6',
            },
            height: '48px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 700,
            width: '100%',
            mb: isSM ? '24px' : null,
            my: '20px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
}

export default CompanyOnboardingStep3;
